import { create } from "zustand";
import { OptionValueType } from "../enums";
import { DraggableItem } from "../components/draggableItem/types";


interface ExpansionRecord {
  itemId: string;
  parentId: string | null;
  level: number;
  timestamp: number;
  label: string;
}

interface CatalogState {
  subValueTypes: { label: string, value: number }[];
  setSubValueTypes: (values: { label: string, value: number }[]) => void;
  subCatalog: { label: string, value: number, children:{ id:number, name:string }[] }[];
  setSubCatalog: (values: { label: string, value: number, children:{ id:number, name:string }[] }[]) => void;

  //CRUD elementos del arbol
  items: DraggableItem[];
  setItems: (items: DraggableItem[]) => void;
  catalogRoot : number | undefined;
  setCatalogRoot: (id: number | undefined) => void;
  addItem: (parentId: string | null, item: DraggableItem | DraggableItem[]) => void;
  editItem: (id: string, label: string, textInput: boolean) => void;
  editItemSubCatalog: (id: string, subCatalog?:number) => void;
  deleteItem: (id: string) => void;
  moveItem: (parentId: string | null, reordered: DraggableItem[]) => void;
  selectedItem: { [parentId: string]: DraggableItem | null };
  setSelectedItem: (parentId: string, item: DraggableItem | null) => void;
  clearChildren: (parentId: string | null) => void;

  // Control de expansión de ramas
  setExpandedLevel: (itemId: string) => void;
  collapseItemAndChildren: (itemId: string) => void;
  
}


export const useCatalogStore = create<CatalogState>((set, get) => ({

  subValueTypes: [{ label: "Ingresar valor", value: OptionValueType.VALUE }, { label: "Seleccionar subcatálogo", value: OptionValueType.SUB_CATALOG }],
  setSubValueTypes: (values) => set({ subValueTypes: values }),
  subCatalog: [],
  setSubCatalog: (values) => set({ subCatalog: values }),
  catalogRoot: undefined,

  //CRUD elementos del arbol
  items: [],
  setItems: (items) => set({ items }),

  addItem: (parentId, newItems) => {
    const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];

    const addRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === parentId
          ? {
            ...item,
            children: [...(item.children || []), ...itemsToAdd],
          }
          : {
            ...item,
            children: item.children ? addRecursive(item.children) : [],
          }
      );

    const currentItems = get().items;

    if (parentId) {
      set({ items: addRecursive(currentItems) });
    } else {
      set({ items: [...currentItems, ...itemsToAdd] });
    }

  },

  setCatalogRoot: (id) => {
    set({ catalogRoot: id });
  },

  editItem: (id, newLabel, newTextInput) => {
    const editRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === id
          ? { ...item, label: newLabel, textInput: newTextInput}
          : { ...item, children: item.children ? editRecursive(item.children) : [] }
      );

    set({ items: editRecursive(get().items) });
  },

  editItemSubCatalog: (id, subCatalog) => {
    const editRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === id
          ? {
            ...item,
            subcatalog: subCatalog
          }
          : { ...item, children: item.children ? editRecursive(item.children) : [] }
      );

    set({ items: editRecursive(get().items) });
  },

  deleteItem: (id: string) => {
    set((state) => {
      //Eliminar el item del árbol de items
      const deleteRecursive = (list: DraggableItem[]): DraggableItem[] =>
        list
          .filter((item) => item.id !== id)
          .map((item) => ({
            ...item,
            children: item.children ? deleteRecursive(item.children) : [],
          }));

      const updatedItems = deleteRecursive(state.items);


      const updatedSelectedItems = Object.fromEntries(
        Object.entries(state.selectedItem).filter(
          ([, selected]) => selected?.id !== id // eliminar solo el que coincide
        )
      );

      return {
        ...state,
        items: updatedItems,
        //selectedItem: isSelectedDeleted ? null : state.selectedItem,
        selectedItem: updatedSelectedItems,
        //selectedItemRoot: isSelectedDeleted ? null : state.selectedItemRoot,
      };
    });

  },

  moveItem: (parentId, reordered) => {
    if (!parentId) {
      set({ items: reordered });
      return;
    }

    const moveRecursive = (list: DraggableItem[]): DraggableItem[] =>
      list.map((item) =>
        item.id === parentId
          ? { ...item, children: reordered }
          : { ...item, children: item.children ? moveRecursive(item.children) : [] }
      );

    set({ items: moveRecursive(get().items) });
  },

  clearChildren: (parentId) => {
    if (parentId) {
      const clearRecursive = (list: DraggableItem[]): DraggableItem[] =>
        list.map((item) =>
          item.id === parentId
            ? { ...item, children: [] }
            : { ...item, children: item.children ? clearRecursive(item.children) : [] }
        );

      set({ items: clearRecursive(get().items) });
    } else {
      set({ items: [] });
    }
  },

  selectedItem: {},
  setSelectedItem: (parentId, item) =>
    set((state) => ({
      selectedItem: {
        ...state.selectedItem,
        [parentId]: item,
      },
    })),

  // Control de expansión de ramas con historial por rama raíz
  expansionHistory: {},

  setExpandedLevel: (itemId: string) => {
    set((state) => {

      //Colapsar todo un subárbol
      const collapseAll = (item: DraggableItem): DraggableItem => ({
        ...item,
        expand: false,
        children: item.children.map(collapseAll),
      });

      //Buscar nodo y actualizar expand
      const updateExpandState = (items: DraggableItem[]): DraggableItem[] =>
        items.map(item => {
          if (item.id === itemId) {
            const isExpanding = !item.expand;

            // Si se está colapsando, cerrar recursivamente a todos los hijos
            return isExpanding
              ? { ...item, expand: true }
              : collapseAll({ ...item, expand: false });
          }

          // Recursividad en hijos
          return { ...item, children: updateExpandState(item.children) };
        });

      const updatedItems = updateExpandState(state.items);



      return {
        ...state,
        items: updatedItems,
      };
    });
  },

  collapseItemAndChildren: (itemId: string) => {
    set((state) => {
      //Función para colapsar todo un subárbol
      const collapseAll = (item: DraggableItem): DraggableItem => ({
        ...item,
        expand: false,
        children: item.children.map(collapseAll),
      });

      //Buscar y colapsar el ítem dentro del árbol
      const collapseRecursive = (items: DraggableItem[]): DraggableItem[] =>
        items.map((item) => {
          if (item.id === itemId) {
            return collapseAll(item);
          }
          return {
            ...item,
            children: item.children ? collapseRecursive(item.children) : [],
          };
        });

      const updatedItems = collapseRecursive(state.items);

      return {
        ...state,
        items: updatedItems,
      };
    });
  },

}));